import { useEffect, useRef } from "react";

interface HeroCard {
  slot: HTMLElement;
  link: HTMLAnchorElement;
  media: HTMLElement;
}

export const useHomeHeroMotion = () => {
  const ref = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root || !window.matchMedia) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const slots = Array.from(root.querySelectorAll<HTMLElement>(".home-hero-product"));
    const cards = new Map<HTMLAnchorElement, HeroCard>(slots.map((slot) => {
      const link = slot.querySelector<HTMLAnchorElement>(".home-hero-product-link")!;
      const media = link.querySelector<HTMLElement>(".home-hero-product-media")!;
      return [link, { slot, link, media }];
    }));
    const entrances = new Map<HTMLElement, Animation>();
    let observer: IntersectionObserver | undefined;
    let entered = reducedMotion.matches;
    let active: HeroCard | undefined;
    let bounds: DOMRect | undefined;
    let pointerX = 0;
    let pointerY = 0;
    let frame = 0;

    const resetPointer = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      active?.slot.classList.remove("is-pointer-active");
      active?.link.style.removeProperty("transform");
      active?.media.style.removeProperty("transform");
      active = undefined;
      bounds = undefined;
    };

    const cancelEntrances = () => {
      entrances.forEach((animation) => animation.cancel());
      entrances.clear();
    };

    if (!reducedMotion.matches && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver((entries) => {
        if (entered || !entries.some((entry) => entry.isIntersecting)) return;
        entered = true;
        observer?.disconnect();
        if (reducedMotion.matches) return;

        // Keep the server HTML visible; the temporary entry layer is separate
        // from the card's base rotation and pointer movement.
        slots.forEach((slot, index) => {
          if (!slot.animate || slot === active?.slot || slot.contains(document.activeElement)) return;
          const animation = slot.animate([
            { opacity: 0.15, transform: "translateY(22px) scale(.98)" },
            { opacity: 1, transform: "translateY(0) scale(1)" },
          ], {
            duration: 600,
            delay: index * 85,
            easing: "cubic-bezier(.2,.75,.25,1)",
            fill: "backwards",
          });
          entrances.set(slot, animation);
          animation.onfinish = animation.oncancel = () => entrances.delete(slot);
        });
      }, { threshold: 0.25 });
      observer.observe(root);
    }

    const cardFromTarget = (target: EventTarget | null) => {
      const link = target instanceof Element
        ? target.closest<HTMLAnchorElement>(".home-hero-product-link")
        : null;
      return link ? cards.get(link) : undefined;
    };

    const updatePointer = () => {
      frame = 0;
      if (!active) return;
      bounds ??= active.slot.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      const x = Math.max(-0.5, Math.min(0.5, (pointerX - bounds.left) / bounds.width - 0.5));
      const y = Math.max(-0.5, Math.min(0.5, (pointerY - bounds.top) / bounds.height - 0.5));
      active.link.style.transform = `translateY(calc(var(--card-offset) - 5px)) rotate(var(--card-rotation)) rotateX(${-y * 8}deg) rotateY(${x * 8}deg)`;
      active.media.style.transform = `translate(${x * 6}px, ${y * 6}px)`;
    };

    const onPointerMove = (event: PointerEvent) => {
      if (reducedMotion.matches || !finePointer.matches || event.pointerType !== "mouse") {
        resetPointer();
        return;
      }
      const card = cardFromTarget(event.target);
      if (!card) {
        resetPointer();
        return;
      }
      if (active !== card) {
        resetPointer();
        entrances.get(card.slot)?.cancel();
        active = card;
        card.slot.classList.add("is-pointer-active");
      }
      pointerX = event.clientX;
      pointerY = event.clientY;
      if (!frame) frame = requestAnimationFrame(updatePointer);
    };

    const onPointerOut = (event: PointerEvent) => {
      if (!(event.relatedTarget instanceof Node) || !active?.link.contains(event.relatedTarget)) {
        resetPointer();
      }
    };

    const onFocus = (event: FocusEvent) => {
      const card = cardFromTarget(event.target);
      if (card) entrances.get(card.slot)?.cancel();
    };

    const invalidateBounds = () => { bounds = undefined; };
    const onPreferenceChange = () => {
      resetPointer();
      if (reducedMotion.matches) {
        entered = true;
        observer?.disconnect();
        cancelEntrances();
      }
    };

    root.addEventListener("pointermove", onPointerMove, { passive: true });
    root.addEventListener("pointerout", onPointerOut);
    root.addEventListener("pointerleave", resetPointer);
    root.addEventListener("pointercancel", resetPointer);
    root.addEventListener("focusin", onFocus);
    root.addEventListener("focusout", resetPointer);
    window.addEventListener("blur", resetPointer);
    window.addEventListener("resize", invalidateBounds, { passive: true });
    window.addEventListener("scroll", invalidateBounds, { capture: true, passive: true });
    reducedMotion.addEventListener("change", onPreferenceChange);
    finePointer.addEventListener("change", onPreferenceChange);

    return () => {
      observer?.disconnect();
      cancelEntrances();
      resetPointer();
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerout", onPointerOut);
      root.removeEventListener("pointerleave", resetPointer);
      root.removeEventListener("pointercancel", resetPointer);
      root.removeEventListener("focusin", onFocus);
      root.removeEventListener("focusout", resetPointer);
      window.removeEventListener("blur", resetPointer);
      window.removeEventListener("resize", invalidateBounds);
      window.removeEventListener("scroll", invalidateBounds, true);
      reducedMotion.removeEventListener("change", onPreferenceChange);
      finePointer.removeEventListener("change", onPreferenceChange);
    };
  }, []);

  return ref;
};
